import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { SectionHeader } from '../common/SectionHeader';
import { Checkbox } from '../common/form/Checkbox';
import { DatePicker } from '../common/form/DatePicker';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { SubmitButton } from '../common/form/SubmitButton';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { LanguageSelectMultiple } from '../common/form/LanguageSelectMultiple';
import { getBusinessSettings, selectBusinessSettings, selectBusinessSettingsState } from '../../store/entities/bussinesSettings';
import { getCompanyCustomization } from '../../store/entities/companyCustomization';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { Link as QcsLink, useTheme } from '@mui/material';
import { CompanyBusinessSettings } from '@api/models/settings/CompanyBusinessSettings';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';
import { companySettingsService } from '@api/services/services';
import { ShowResourceContent } from '../ShowResourceContent';

const COMPANY_URL = import.meta.env.VITE_COMPANY_URL as string;

export const QcsCompanySettings: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const businessSettings = useAppSelector(selectBusinessSettings);

  const handleSubmit = async (data: CompanyBusinessSettings) => {
    await companySettingsService.updateBusinessSettings(data);

    dispatch(getBusinessSettings());
    dispatch(getCompanyCustomization());
  };

  const getSubDomainUrl = useMemo(() => (domain?: string) => COMPANY_URL.replaceAll('{subdomain}', domain ?? ''), []);

  return (
    <>
      <SectionHeader title="qcsCompanySettings.title" />
      <ShowResourceContent resource={resources.updateBusinessSettings} fetchStateSelector={selectBusinessSettingsState}>
        <FormikDebounce<CompanyBusinessSettings>
          initialValues={businessSettings}
          enableReinitialize
          validationSchema={Yup.object().shape({
            licenseValidFrom: validations.stringDateNotPastIfChangedRequired(t, businessSettings.licenseValidFrom),
            licenseValidTo: Yup.string()
              .nullable()
              .when('licenseValidFrom', ([licenseValidFrom], schema) =>
                schema.test({
                  test: (licenseValidTo: string | null | undefined) => {
                    return !licenseValidTo || licenseValidFrom <= licenseValidTo;
                  },
                  message: t('errors.dateToBeforeFrom')!,
                }),
              ),
            maxEstablishments: validations.intPositive(t),
            maxUsers: validations.intPositive(t),
            maxTraining: validations.intPositive(t),
            applicationLanguages: validations.arrayNotEmpty(t),
          })}
          onSubmit={handleSubmit}
        >
          {({ values }) => {
            return (
              <Form>
                <FormContainer>
                  <DatePicker name="licenseValidFrom" label={t('admin.company.licenseValidFrom')} disablePast required />
                  <DatePicker name="licenseValidTo" label={t('admin.company.licenseValidTo')} disablePast />
                  <Input name="maxEstablishments" label={t('admin.company.maxEstablishments')} type="number" min={1} required />
                  <Input name="maxUsers" label={t('admin.company.maxUsers')} type="number" min={1} required />
                  <Input name="maxTraining" label={t('admin.company.maxTraining')} type="number" min={1} required />
                  <QcsLink
                    href={getSubDomainUrl(values.subDomain)}
                    variant="body1"
                    target="_blank"
                    sx={{
                      color: theme.palette.text.primary,
                      textDecoration: 'none',
                    }}
                  >
                    {getSubDomainUrl(values.subDomain)}
                  </QcsLink>
                  <Checkbox name="passRisksEnabled" label={t('admin.company.passRisksEnabled')} />
                  <Checkbox name="workplaceHandoverEnabled" label={t('admin.company.enableHandover')} />
                  <Checkbox name="turnstilesEnabled" label={t('admin.company.turnstilesEnabled')} />
                  <Checkbox name="allowEntryRequests" label={t('admin.company.allowEntryRequests')} />
                  <Checkbox name="forTesting" label={t('admin.company.forTesting')} />
                  <Checkbox name="allowAnalytics" label={t('admin.company.allowAnalytics')} />
                  <Checkbox name="allowSubcontractors" label={t('admin.company.allowSubcontractors')} />
                  <LanguageSelectMultiple
                    name="applicationLanguages"
                    label={t('admin.company.applicationLanguages')}
                    languages={SUPPORTED_LANGUAGES}
                    required
                  />
                  <ShowError resource={resources.updateBusinessSettings} />
                  <SubmitButton />
                </FormContainer>
              </Form>
            );
          }}
        </FormikDebounce>
      </ShowResourceContent>
    </>
  );
};
